import { Box, Link, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ContextStore } from '../store/contextStore';
import { ACTIVITY_GROUPS, ActivityItem, activityHref, activityType } from './activityTypes';
import { useDeveloperApi } from './api';
import { DeveloperShell, EnvironmentChip, ErrorAlert, LoadMore, Loading, mono, TableScroll, Timestamp, ToneChip } from './components';
import { useCursorList } from './useCursorList';

const ActorCell = ({ item }: { item: ActivityItem }) => {
    if (!item.actor) {
        return <Typography variant="body2" color="text.secondary">Automatic</Typography>;
    }
    return (
        <Box>
            <Typography variant="body2">{item.actor.name}</Typography>
            {item.actor.email && item.actor.email !== item.actor.name && (
                <Typography variant="caption" color="text.secondary" component="div">{item.actor.email}</Typography>
            )}
            {item.ip && <Typography variant="caption" color="text.secondary" component="div" sx={mono}>{item.ip}</Typography>}
        </Box>
    );
};

const OpenLink = ({ item, environment }: { item: ActivityItem; environment: string }) => {
    const target = activityHref(item, environment);
    if (!target) {
        return null;
    }
    const label = target.href ? `Open in ${item.tenant?.environment === 'sandbox' ? 'the sandbox' : 'production'}` : 'Open';
    return target.href
        ? <Link href={target.href} target="_blank" rel="noopener noreferrer" variant="body2">{label}</Link>
        : <Link component={RouterLink} to={target.to as string} variant="body2">{label}</Link>;
};

/** The Activity (DSEC D2; DEVELOPER-SECTION-PLAN §4): what happened to this tenant's keys, sandbox,
 *  webhooks and MCP connections, newest first -- production's page includes its sandbox's. The former
 *  Developer notifications are here too, and are still e-mailed to the developer contacts. */
const ActivityPage = () => {
    const api = useDeveloperApi();
    const { environment: current } = React.useContext(ContextStore) as any;
    const environment = current || 'production';
    const [group, setGroup] = React.useState('');
    const [tenant, setTenant] = React.useState('');
    const [from, setFrom] = React.useState('');
    const [to, setTo] = React.useState('');

    const types = ACTIVITY_GROUPS.find((g) => g.key === group)?.types.join(',') || '';
    const since = from ? moment(from).startOf('day').toISOString() : '';
    const until = to ? moment(to).add(1, 'day').startOf('day').toISOString() : '';
    const list = useCursorList<ActivityItem>(api.activity, { type: types, tenant, since, until });

    return (
        <DeveloperShell title="Activity">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {environment === 'production'
                    ? 'What happened to your API keys, sandbox, webhooks and MCP connections, newest first, in production and in your sandbox.'
                    : 'What happened to this sandbox, its API keys and webhooks, newest first.'}
                {' '}The last 90 days unless you pick dates. Sandbox alerts are also e-mailed to your developer contacts.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1.5 }}>
                <TextField select size="small" label="Type" value={group} onChange={(ev) => setGroup(ev.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="">Any</MenuItem>
                    {ACTIVITY_GROUPS.map((g) => <MenuItem key={g.key} value={g.key}>{g.label}</MenuItem>)}
                </TextField>
                {environment === 'production' && (
                    <TextField select size="small" label="Environment" value={tenant} onChange={(ev) => setTenant(ev.target.value)} sx={{ minWidth: 170 }}>
                        <MenuItem value="">Both</MenuItem>
                        <MenuItem value="production">Production</MenuItem>
                        <MenuItem value="sandbox">Sandbox</MenuItem>
                    </TextField>
                )}
                <TextField size="small" type="date" label="From" value={from} onChange={(ev) => setFrom(ev.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={to} onChange={(ev) => setTo(ev.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
            {list.error ? <ErrorAlert error={list.error} onRetry={list.reload} /> : null}
            {list.loading && list.items.length === 0 ? <Loading /> : (
                <TableScroll>
                    <Table size="small" data-testid="developer-activity">
                        <TableHead>
                            <TableRow>
                                <TableCell>When</TableCell>
                                {environment === 'production' && <TableCell>Environment</TableCell>}
                                <TableCell>Event</TableCell>
                                <TableCell>By</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.items.map((item) => {
                                const kind = activityType(item.type);
                                return (
                                    <TableRow key={item.id} data-testid={`activity-${item.id}`}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}><Timestamp value={item.at} /></TableCell>
                                        {environment === 'production' && (
                                            <TableCell sx={{ verticalAlign: 'top' }}><EnvironmentChip environment={item.tenant?.environment} /></TableCell>
                                        )}
                                        <TableCell sx={{ maxWidth: 560 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2">{item.title || kind.label}</Typography>
                                                <ToneChip label={kind.label} tone={item.severity === 'critical' ? 'error' : kind.tone} hint={kind.hint} />
                                                {item.platform && <ToneChip label="From Curated Planet" tone="neutral" />}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>{item.text}</Typography>
                                            <OpenLink item={item} environment={environment} />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}><ActorCell item={item} /></TableCell>
                                    </TableRow>
                                );
                            })}
                            {!list.loading && list.items.length === 0 && !list.error && (
                                <TableRow>
                                    <TableCell colSpan={environment === 'production' ? 4 : 3}>
                                        <Typography variant="body2" color="text.secondary">Nothing yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableScroll>
            )}
            <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
        </DeveloperShell>
    );
};

export default ActivityPage;
